// LanguageSwitcher.tsx
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { trackAnalyticsEvent } from "../analytics/analytics";

type Flag = {
  code: "en" | "da" | "sv";
  flag: string;
  label: string;
  width: number;
  height: number;
};

const LANGS: Flag[] = [
  { code: "en", flag: "us", label: "English", width: 18, height: 15 },
  { code: "da", flag: "dk", label: "Dansk", width: 18, height: 15 },
  { code: "sv", flag: "se", label: "Svenska", width: 18, height: 15 },
];

// Helper: ensure path has the right /{lng} prefix, preserving rest of path
function withLangPrefix(pathname: string, lng: "en" | "da" | "sv") {
  // Strip any existing /en, /da, or /sv at the start
  const cleaned = pathname.replace(/^\/(en|da|sv)(?=\/|$)/, "");
  // Always prefix with the chosen lang; keep single leading slash
  return `/${lng}${cleaned.startsWith("/") ? cleaned : `/${cleaned}`}`.replace(
    /\/+$/,
    (m) => (m === "/" ? "/" : m)
  );
}

export default function LanguageSwitcher() {
  const { i18n } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();

  // Detect current language
  let current: "en" | "da" | "sv" = "en";
  if (i18n.language?.startsWith("da")) current = "da";
  else if (i18n.language?.startsWith("sv")) current = "sv";

  const [open, setOpen] = useState(false);
  const menuId = useId();
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  const setLang = (lng: "en" | "da" | "sv") => {
    if (current !== lng) {
      trackAnalyticsEvent("language_switch", {
        from_language: current,
        to_language: lng,
      });
    }

    // 1) i18n + localStorage
    i18n.changeLanguage(lng);
    localStorage.setItem("lang", lng);

    // 2) URL rewrite to same route with lang prefix
    const newPath =
      withLangPrefix(location.pathname, lng) +
      (location.search || "") +
      (location.hash || "");
    if (
      newPath !==
      location.pathname + (location.search || "") + (location.hash || "")
    ) {
      navigate(newPath, { replace: true });
    }

    setOpen(false);
  };

  // Close on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!open) return;
      if (
        popRef.current &&
        !popRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Keyboard support
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") setOpen(false);
  };

  const currentLANG = LANGS.find((l) => l.code === current) ?? LANGS[0];

  return (
    <div className="relative" onKeyDown={onKeyDown}>
      {/* Trigger: only flag */}
      <button
        ref={btnRef}
        type="button"
        aria-label="Change language"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        className="grid h-9 w-9 place-items-center overflow-hidden rounded-md border border-slate-200 bg-white transition hover:border-slate-400"
        onClick={() => setOpen((o) => !o)}
      >
        <img
          loading="lazy"
          width={currentLANG.width}
          height={currentLANG.height}
          src={`https://flagcdn.com/w40/${currentLANG.flag}.png`}
          alt=""
          aria-hidden="true"
        />
      </button>

      {/* Popover */}
      {open && (
        <div
          id={menuId}
          ref={popRef}
          role="menu"
          className="absolute right-0 mt-2 w-14 rounded-md border border-slate-200 bg-white p-1 shadow-lg"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              role="menuitem"
              aria-label={l.label}
              onClick={() => setLang(l.code)}
              className="flex h-[30px] w-full items-center justify-center rounded hover:bg-slate-100"
            >
              <img
                loading="lazy"
                width={l.width}
                height={l.height}
                src={`https://flagcdn.com/w40/${l.flag}.png`}
                alt={l.label}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
